import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Autocomplete, ToggleButton, ToggleButtonGroup, Stack, Typography, Box,
  CircularProgress, Divider, Alert,
} from '@mui/material';
import { toast } from 'react-toastify';
import fundedAPI from '../../api/funded';
import { currency, fmtDate } from './format';

// Modal to match an unmatched deposit to an existing client, or create a new one.
export default function MatchDepositModal({ open, deposit, onClose, onMatched }) {
  const [mode, setMode] = useState('existing');
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [newName, setNewName] = useState('');
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setMode('existing'); setSelected(null); setInputValue(''); setNewName(''); setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (mode !== 'existing' || !open) return;
    let active = true;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fundedAPI.getClients({ search: inputValue, limit: 20 });
        if (active) setOptions(res.data.data || []);
      } catch {
        if (active) setOptions([]);
      } finally {
        if (active) setSearching(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [inputValue, mode, open]);

  const handleSubmit = async () => {
    if (!deposit) return;
    const payload = mode === 'existing'
      ? { client_id: selected?.client_id }
      : { createClient: true, business_name: newName.trim() };

    if (mode === 'existing' && !payload.client_id) return toast.error('Select a client');
    if (mode === 'new' && !payload.business_name) return toast.error('Enter a business name');

    setSaving(true);
    try {
      await fundedAPI.matchDeposit(deposit.deposit_id, payload);
      toast.success('Deposit matched — funding & commission created');
      onMatched?.();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to match deposit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Match Deposit to Client</DialogTitle>
      <DialogContent>
        {deposit && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Box><Typography variant="caption" color="text.secondary">Amount</Typography><Typography fontWeight={700}>{currency(deposit.amount)}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Date</Typography><Typography>{fmtDate(deposit.date)}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Lender</Typography><Typography>{deposit.lender || '—'}</Typography></Box>
            </Stack>
            {deposit.sender && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{deposit.sender}</Typography>
            )}
          </Box>
        )}

        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(_, v) => v && setMode(v)}
          sx={{ mb: 2 }}
          fullWidth
        >
          <ToggleButton value="existing">Link to existing client</ToggleButton>
          <ToggleButton value="new">Create new client</ToggleButton>
        </ToggleButtonGroup>

        {mode === 'existing' ? (
          <Autocomplete
            options={options}
            value={selected}
            onChange={(_, v) => setSelected(v)}
            inputValue={inputValue}
            onInputChange={(_, v) => setInputValue(v)}
            getOptionLabel={(o) => o.business_name || o.client_id || ''}
            isOptionEqualToValue={(o, v) => o.client_id === v.client_id}
            loading={searching}
            renderOption={(props, o) => (
              <li {...props} key={o.client_id}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{o.business_name || o.client_id}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {currency(o.totalCommissions)} · {o.fundingCount} fundings
                  </Typography>
                </Box>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search clients by name, EIN, email"
                autoFocus
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (<>{searching ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>),
                }}
              />
            )}
          />
        ) : (
          <>
            <TextField
              label="Business name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              fullWidth
              autoFocus
            />
            <Alert severity="info" sx={{ mt: 1.5 }}>
              A new funded client will be created and keyed by normalized name. You can link later matches by EIN or email.
            </Alert>
          </>
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          This creates matching funding &amp; commission records and is recorded in the activity log.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Matching…' : 'Match Deposit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
